import type { FieldValues, Resolver } from 'react-hook-form';

import type { ApiFieldError, ApiFieldErrorCode, ApiFieldErrorParams } from '@/api/error.api';
import { translateFile, translatedMap } from '@/i18n';
import { getValueByPath, isEqual, setValueByPath } from '@/utils/object';

const FIELD_ERROR_MESSAGES = translatedMap('utils.errors', {
  REQUIRED: 'required',
  INVALID_VALUE: 'invalidValue',
  MIN_LENGTH: 'minLength',
  MAX_LENGTH: 'maxLength',
  MIN_ITEMS: 'minItems',
  MAX_ITEMS: 'maxItems',
  MIN_VALUE: 'minValue',
  MAX_VALUE: 'maxValue',
  NOT_INTEGER: 'notInteger',
  INVALID_URL: 'invalidUrl',
  INVALID_EMAIL: 'invalidEmail',
  INVALID_ID: 'invalidId',
  INVALID_FORMAT: 'invalidFormat',
  UNKNOWN_FIELD: 'unknownField',
  DATE_IN_FUTURE: 'dateInFuture',
  DATE_IN_PAST: 'dateInPast',
  INVALID_PHONE: 'invalidPhone',
  INVALID_TAX_ID: 'invalidTaxId',
  INVALID_POSTAL_CODE: 'invalidPostalCode',
  ALREADY_EXISTS: 'alreadyExists',
}) as Record<ApiFieldErrorCode, string>;

const GLOBAL_ERROR_MESSAGES = translatedMap('utils.errors', {
  VALIDATION_ERROR: 'validationError',
}) as Record<string, string | undefined>;

export const getGenericFieldErrorMessage = () => translateFile('utils.errors', 'invalidValue');

export const getGenericGlobalErrorMessage = () => translateFile('utils.errors', 'genericGlobal');

const interpolate = (template: string, params?: ApiFieldErrorParams) =>
  template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const numericParams: Record<string, number | undefined> = {
      min: params?.min,
      max: params?.max,
    };
    const value = numericParams[key];
    if (value === undefined) return match;
    return String(value);
  });

export const translateFieldError = (fieldError: ApiFieldError): string => {
  const template = FIELD_ERROR_MESSAGES[fieldError.code as ApiFieldErrorCode];

  if (template) return interpolate(template, fieldError.params);
  if (fieldError.message) return fieldError.message;

  return getGenericFieldErrorMessage();
};

export const partitionApiFieldErrors = (
  fieldErrors: ApiFieldError[],
  isVisibleField: (path: string) => boolean,
): { fieldMessages: Record<string, string>; orphanMessages: string[] } => {
  const fieldMessages: Record<string, string> = {};
  const orphanMessages: string[] = [];

  fieldErrors.forEach((fieldError) => {
    const message = translateFieldError(fieldError);

    if (!isVisibleField(fieldError.field)) {
      orphanMessages.push(message);
      return;
    }

    if (!fieldMessages[fieldError.field]) fieldMessages[fieldError.field] = message;
  });

  return { fieldMessages, orphanMessages };
};

export const translateGlobalError = ({
  code,
  userMessage,
  fallbackMessage,
}: {
  code?: string;
  userMessage?: string;
  fallbackMessage?: string;
}): string => {
  if (userMessage) return userMessage;
  if (code && GLOBAL_ERROR_MESSAGES[code]) return GLOBAL_ERROR_MESSAGES[code];

  return fallbackMessage || getGenericGlobalErrorMessage();
};

type StoredServerError = {
  message: string;
  value: unknown;
};

export type ServerErrorsStore = {
  set: (path: string, message: string, value: unknown) => void;
  remove: (path: string) => void;
  clear: () => void;
  isEmpty: () => boolean;
};

type InternalServerErrorsStore = ServerErrorsStore & {
  entries: () => [string, StoredServerError][];
};

export const createServerErrorsStore = (): InternalServerErrorsStore => {
  const errors = new Map<string, StoredServerError>();

  return {
    set: (path, message, value) => errors.set(path, { message, value }),
    remove: (path) => {
      errors.delete(path);
    },
    clear: () => errors.clear(),
    isEmpty: () => errors.size === 0,
    entries: () => [...errors.entries()],
  };
};

/** Keep API field errors until their own field value changes. */
export const withServerErrors = <TFieldValues extends FieldValues, TContext = unknown>(
  resolver: Resolver<TFieldValues, TContext>,
  store: ServerErrorsStore,
): Resolver<TFieldValues, TContext> => {
  const internalStore = store as InternalServerErrorsStore;

  return async (values, context, options) => {
    const result = await resolver(values, context, options);

    const surviving = internalStore.entries().filter(([path, stored]) => {
      if (isEqual(getValueByPath(values, path), stored.value)) return true;
      internalStore.remove(path);
      return false;
    });

    if (!surviving.length) return result;

    const errors = { ...result.errors } as Record<string, unknown>;

    surviving.forEach(([path, stored]) => {
      if (getValueByPath(errors, path)) return;
      setValueByPath(errors, path, { type: 'server', message: stored.message });
    });

    return { values: {} as TFieldValues, errors } as Awaited<
      ReturnType<Resolver<TFieldValues, TContext>>
    >;
  };
};
